import * as React from 'react';
import {
  List,
  Datagrid,
  Edit,
  Create,
  SimpleForm,
  TextField,
  EditButton,
  TextInput,
  BooleanInput,
  FunctionField,
  useTranslate,
  useLocaleState,
  required,
  useEditContext,
  useRecordContext,
} from 'react-admin';
import { Box } from '@mui/material';

import EmptyList from '../components/misc/EmptyList';
import SourceLanguageBanner from '../components/form/SourceLanguageBanner';
import { useSourceLanguage } from '../hooks/useSourceLanguage';
import { EditableImage } from '../components/form/EditableImageField';
import { resolveMediaUrl } from '../utils/mediaUrl';

const entity = 'categories';
const ICON_SIZE = 96;
const ICON_ASPECT = 1;

const CategoryIconPreview = () => {
  const record = useRecordContext();
  const icon = record?.icon;
  let src = null;
  if (typeof icon === 'string') src = resolveMediaUrl(icon);
  else if (icon?.src) src = resolveMediaUrl(icon.src);
  if (!src) return <span style={{ color: '#999', fontSize: 12 }}>—</span>;
  return (
    <Box
      component="img"
      src={src}
      alt=""
      sx={{ width: 28, height: 28, objectFit: 'contain', display: 'block' }}
    />
  );
};

export const CategoryList = (props) => {
  const translate = useTranslate();
  const [locale] = useLocaleState();
  const localeNameKey = locale === 'tr' ? 'name.tr' : 'name.en';

  return (
    <List
      {...props}
      title={translate('categories.title', { _: 'Kategoriler' })}
      empty={<EmptyList entity={entity} />}
      exporter={false}
      sort={{ field: 'order', order: 'ASC' }}
    >
      <Datagrid>
        <FunctionField
          label={translate('categories.icon', { _: 'İkon' })}
          render={() => <CategoryIconPreview />}
        />
        <TextField
          source={localeNameKey}
          label={translate('categories.name', { _: 'Kategori Adı' })}
        />
        <FunctionField
          source="active"
          label={translate('categories.active', { _: 'Aktif mi?' })}
          render={(r) =>
            r.active
              ? translate('categories.yes', { _: 'Evet' })
              : translate('categories.no', { _: 'Hayır' })
          }
        />
        <TextField source="order" label={translate('categories.order', { _: 'Sıra' })} />
        <EditButton />
      </Datagrid>
    </List>
  );
};

const CategoryFormFields = ({ isEdit = false, record }) => {
  const translate = useTranslate();
  const { localizedSource } = useSourceLanguage({ isEdit, record });

  return (
    <>
      <SourceLanguageBanner isEdit={isEdit} record={record} />
      <TextInput source="id" label="ID" disabled={isEdit} validate={required()} />
      {isEdit ? (
        <BooleanInput
          source="active"
          label={translate('categories.active', { _: 'Aktif mi?' })}
          helperText={translate('categories.active_help', {
            _: 'Bu kategoriyi devre dışı bırakmak istiyorsanız bu seçeneğin işaretini kaldırın.',
          })}
        />
      ) : (
        <BooleanInput source="active" defaultValue={true} sx={{ display: 'none' }} />
      )}
      <TextInput
        source={localizedSource('name')}
        label={translate('categories.name', { _: 'Kategori Adı' })}
        fullWidth
        validate={required()}
      />
      <TextInput
        source="order"
        label={translate('categories.order', { _: 'Sıra' })}
        fullWidth
        helperText={translate('categories.order_help', {
          _: 'Kategorinin sıralamasını belirlemek için bir sayı giriniz.',
        })}
      />
      <EditableImage
        source="icon"
        record={record}
        aspect={ICON_ASPECT}
        width={ICON_SIZE}
        height={ICON_SIZE}
        label={translate('categories.icon', { _: 'İkon' })}
        hint={translate('categories.icon_hint', {
          _: 'Boş bırakılırsa sitedeki varsayılan ikon kullanılır. Yükleyince panel ikonu geçerli olur. Kare (1:1) önerilir.',
        })}
        cropTitle={translate('categories.icon_crop_title', { _: 'Kategori İkonu Kırp (1:1)' })}
        cropDesc={translate('categories.icon_crop_desc', {
          _: 'Kırpma alanı kare oranında kilitli.',
        })}
      />
    </>
  );
};

const CategoryEditForm = () => {
  const { record } = useEditContext();
  return (
    <SimpleForm redirect={`/${entity}`}>
      <CategoryFormFields isEdit record={record} />
    </SimpleForm>
  );
};

export const CategoryEdit = (props) => {
  const translate = useTranslate();

  return (
    <Edit
      title={translate('categories.edit_category', { _: 'Kategoriyi Düzenle' })}
      {...props}
      transform={(data, { previousData }) => ({
        ...data,
        sourceLang: previousData?.sourceLanguage || 'tr',
      })}
    >
      <CategoryEditForm />
    </Edit>
  );
};

export const CategoryCreate = (props) => {
  const translate = useTranslate();
  const [locale] = useLocaleState();

  return (
    <Create
      title={translate('categories.new_category', { _: 'Yeni Kategori' })}
      {...props}
      transform={(data) => ({
        ...data,
        sourceLang: locale || 'tr',
      })}
    >
      <SimpleForm redirect={`/${entity}`}>
        <CategoryFormFields isEdit={false} />
      </SimpleForm>
    </Create>
  );
};
